import SBRebuy from "./sb-rebuy.js";

class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector("sticky-header");
    this.drawer = document.querySelector("cart-drawer");
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        document.getElementById("mini-cart").innerHTML = this.getSectionInnerHTML(html, ".shopify-section");

        document.dispatchEvent(new CustomEvent("cartdrawer:opened"));
      })
      .catch((e) => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector("details");
    if (detailsElement.hasAttribute("open")) {
      return;
    }

    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (document.getElementById(section.id)) {
        document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector,
        );
      }
    });
    this.open();
    this.querySelector("sb-cart-upsell[data-rebuy-enabled='true']")?.fetchRebuyProducts();
  }

  getSectionsToRender() {
    return [
      {
        id: "mini-cart",
        section: "mini-cart",
        selector: ".shopify-section",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "mobile-cart-icon-bubble",
        section: "mobile-cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("mini-cart", MiniCart);

if (!customElements.get("sb-cart-upsell")) {
  class SBCartUpsell extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this._rebuyFetchPromise = null;
      this.init = this.init.bind(this);
      this.destroy = this.destroy.bind(this);
    }

    connectedCallback() {
      if (this.dataset.rebuyEnabled !== "true") {
        this.finishLoading();
        return;
      }

      if (this.dataset.emptyCartUpsell === "true") {
        this.fetchRebuyProducts();
      }

      if (window.Swiper) {
        this.init();
      } else {
        const interval = setInterval(() => {
          if (window.Swiper) {
            clearInterval(interval);
            this.init();
          }
        }, 50);
      }
    }

    init() {
      const swiperContainer = this.querySelector(".swiper");
      if (!swiperContainer || swiperContainer.classList.contains("swiper-initialized")) return;

      const slides = this.querySelectorAll(".swiper-slide");
      if (slides.length <= 1) return;

      const breakpoints = {
        360: {
          slidesPerView: 1.5,
          spaceBetween: 16,
        },
        750: {
          slidesPerView: 2.5,
          spaceBetween: 16,
        },
      };

      const swiperOptions = {
        slidesPerView: 5,
        spaceBetween: 16,
        breakpoints,
        watchOverflow: false,
        grabCursor: false,
        loop: false,
        navigation: {
          nextEl: swiperContainer.querySelector(".custom-next"),
          prevEl: swiperContainer.querySelector(".custom-prev"),
        },
      };

      this.swiper = new window.Swiper(swiperContainer, swiperOptions);
      swiperContainer.classList.add("swiper-initialized");
    }

    destroy() {
      if (this.swiper && this.swiper.destroy) {
        this.swiper.destroy();
        this.swiper = null;
        const swiperContainer = this.closest(".swiper");
        if (swiperContainer) {
          swiperContainer.classList.remove("swiper-initialized");
        }
      }
    }

    finishLoading() {
      this.init();
      const wrapper = this.querySelector(".cart-upsell__inner .swiper");
      wrapper?.classList.remove("loading");
      this.querySelector(".loading-bar")?.classList.add("hidden");
    }

    getActiveDepartmentName() {
      return (
        document
          .querySelector(".sb-header__department.active .sb-header__department-inner")
          ?.dataset?.departmentName?.toLowerCase() || null
      );
    }

    getEmptyCartWidgetId() {
      const departmentName = this.getActiveDepartmentName();
      if (!departmentName) return null;

      const widgetIds = {
        beauty: this.dataset.widgetIdBeauty,
        toys: this.dataset.widgetIdToys,
        home: this.dataset.widgetIdHome,
      };

      return widgetIds[departmentName] || null;
    }

    async fetchRebuyProducts() {
      if (this.dataset.rebuyEnabled !== "true") return;
      if (this._rebuyFetchPromise) return this._rebuyFetchPromise;

      this._rebuyFetchPromise = this.loadRebuyProducts();
      return this._rebuyFetchPromise;
    }

    async loadRebuyProducts() {
      let rebuyProductType = this.dataset.rebuyProductType;
      let rebuyCustomEndpointId = this.dataset.rebuyCustomEndpointId || false;
      const productLimit = this.dataset.productLimit;
      const rebuyDynamicProduct = this.dataset.rebuyDynamicProduct;
      const customerId = this.dataset.customerId;

      if (this.dataset.emptyCartUpsell === "true") {
        const widgetId = this.getEmptyCartWidgetId();
        if (!widgetId) {
          this.finishLoading();
          return;
        }
        rebuyProductType = "custom_endpoint";
        rebuyCustomEndpointId = widgetId;
      }

      const rebuyInstance = new SBRebuy();
      rebuyInstance.initRebuyVariables(
        rebuyProductType,
        productLimit,
        rebuyDynamicProduct,
        customerId,
        rebuyCustomEndpointId,
      );

      const products_handle = await rebuyInstance.fetchRebuyProducts();
      this.renderProducts(products_handle);
    }

    renderProducts(products_handle) {
      if (!products_handle?.length) {
        this.finishLoading();
        return;
      }

      let completed = 0;
      const total = products_handle.length;
      products_handle.forEach((handle) => {
        this.renderProductDom(handle, () => {
          completed++;
          if (completed === total) {
            this.finishLoading();
          }
        });
      });
    }

    renderProductDom(handle, callback) {
      fetch(`${window.origin}/products/${handle}?section_id=sb-rebuy-product-cart`)
        .then((response) => response.text())
        .then((data) => {
          const productDom = new DOMParser().parseFromString(data, "text/html");
          const card = productDom?.querySelector(".sb-featured-products__card-wrapper");
          if (card) {
            this.querySelector(".cart-upsell__products")?.appendChild(card);
          }
          callback();
        })
        .catch((error) => {
          console.error("Error rendering product DOM:", error);
          callback();
        });
    }
  }

  customElements.define("sb-cart-upsell", SBCartUpsell);
}
